'use client'

import React, { forwardRef, JSX, RefObject, Suspense, useEffect } from 'react'
import { Provider } from 'react-redux'

import { Inter, Karla, Merriweather, Montserrat, Nunito, Open_Sans } from 'next/font/google'

import { OrganizationRegistry, Organizations } from '@/lib/config/organization'
import resolve from '@/lib/config/resolve'
import { ServiceModules } from '@/lib/constants/services'
import Scripts from '@/scripts'
import ClarityCookieConsent from '@/scripts/clarity-cookie-consent'
import { ApiProvider, useApiContext } from '@aw/lib'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'


import { ThemeProvider, useColor } from '@/lib/context/theme'
import { AuthTokenProvider } from '@/lib/context/AuthTokenProvider'
import { useAccount } from '@/lib/hooks/store/useAccount'
import { useAppSelector } from '@/lib/hooks/store/store'
import { AppProgressProvider } from '@bprogress/next'
import store from '@/lib/store'
import { DialogProvider, Toaster } from '@aw/ui'
import { ThemeProvider as DarkModeProvider } from 'next-themes'
import "@/styles/globals.css"

export const nunito = Nunito({
  subsets: ['latin'],
  variable: '--font-nunito',
  display: 'swap'
})

export const montserrat = Montserrat({
  subsets: ['latin'],
  variable: '--font-montserrat',
  display: 'swap'
})

export const karla = Karla({
  subsets: ['latin'],
  variable: '--font-karla',
  display: 'swap',
  preload: true
})

export const OpenSans = Open_Sans({
  subsets: ['latin'],
  variable: '--font-open-sans',
  display: 'swap'
})

export const merriweather = Merriweather({
  weight: "400", // or any other valid weight
  subsets: ["latin"],
  variable: "--font-merriweather",
  display: "swap"
})

export const inter = Inter({
  subsets: ["latin"],
  variable: "--font-inter",
  display: "swap"
})

export type AppLayoutProps = {
  breadcrumb?: () => JSX.Element
  children?: React.ReactNode
}

// eslint-disable-next-line react/display-name
const AppLayout = forwardRef<RefObject<HTMLElement>, AppLayoutProps>(({ children }, ref) => {

  // TODO - use screen hook to detect small device & add body class - overflow-hidden
  const refProp = ref ? { ref: ref } : {}

  const [queryClient] = React.useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5,
        refetchOnWindowFocus: false,
      },
    },
  }))

  const apiConfig = {
    baseUrl: process.env.NEXT_PUBLIC_BACKEND_URL as string,
    services: ServiceModules,
    resolve,
    serviceDomains: {
      [ServiceModules.TALENT_POOL_ATS]: 'https://search.abroadworks.com',
      [ServiceModules.SUBSCRIPTION]: process.env.NEXT_PUBLIC_SUBSCRIPTION_URL as string,
    },
    // ATS AI Search (AWOPS-3073) enforces strict CORS: only Authorization + Content-Type
    // request headers are allowed, and credentials are disallowed on the dev origin.
    // subscription-service (AWOPS-3154) is bearer-token only — no cookies, and it takes the org
    // via a per-call `x-organization-uid` header rather than the shared `X-Organization-Id`.
    corsRestrictedServices: [ServiceModules.TALENT_POOL_ATS, ServiceModules.SUBSCRIPTION],
    // payment-service CORS does not allow X-Organization-Id — skip the header for those routes.
    skipOrganizationHeaderServices: [ServiceModules.PAYMENT],
    defaultOrganization: OrganizationRegistry.ABROADWORKS,
  }
  const organizations = Organizations[OrganizationRegistry.ABROADWORKS]

  return (
    <html suppressHydrationWarning className={`${karla.variable} ${montserrat.variable} ${OpenSans.variable} ${merriweather.variable} ${inter.variable}`}>
      <link rel="icon" type="image/x-icon" href="/images/olamee-thumbnail.svg" />
      <head>
        <Scripts />
      </head>
      <body className="antialiased" >
        <DarkModeProvider
          attribute="class"
          defaultTheme="system"
          enableSystem
          storageKey="olamee-theme"
        >
        <Provider store={store}>
          <QueryClientProvider client={queryClient}>
            <ApiProvider config={apiConfig}>
              <AuthTokenProvider>
                <DialogProvider>
                  <Toaster position="top-center" richColors />
                  <Suspense>
                    <AppProgressProvider
                      options={{ showSpinner: false }}
                      shallowRouting
                      color="#425EBD"
                    >
                      <ThemeProvider>
                        <Main>
                          {children}
                        </Main>
                      </ThemeProvider>
                    </AppProgressProvider>
                  </Suspense>
                </DialogProvider>
              </AuthTokenProvider>
            </ApiProvider>
          </QueryClientProvider>
        </Provider>
        </DarkModeProvider>
      </body >
    </html >
  )
})

export default AppLayout



function Main({ children }: { children: React.ReactNode }) {
  const { user } = useAccount()
  const { setModuleColor } = useColor()
  const currentOrganization = useAppSelector((state) => state.workspace.currentOrganization)
  const { updateConfig } = useApiContext()

  useEffect(() => {
    setModuleColor('find')

  }, [])

  useEffect(() => {
    if (!currentOrganization) return
    updateConfig({ defaultOrganization: String(currentOrganization) })
  }, [currentOrganization, updateConfig])

  return <main className="relative flex justify-items-stretch h-full w-full flex-col bg-canvas text-foreground overflow-hidden">
    <ClarityCookieConsent email={user?.email} />

    <div className="flex-1 min-h-0 w-full overflow-hidden">

      {children}
    </div>
  </main>
}